/**
 * A person's own permission for a workflow to read their mailbox (AMO-169):
 * the switch beside an email source in the editor, and, on a run waiting at
 * an email Intake step, "Allow for this run" or "for every run" for the
 * person the run acts for. Nobody can give it for anyone else; the server
 * checks again. Also the run's log of every mailbox read.
 *
 * @module components/workflows/MailboxAccess
 */
import type {
  WorkflowMailApp,
  WorkflowMailboxAccessRequest,
  WorkflowMailboxAccessResponse,
  WorkflowRun,
} from "@amoreg/contracts";
import { workflowMailApps } from "@repo/shared/workflowGraph";
import { MailIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { readWorkflowMailbox, setWorkflowMailbox } from "../../state/workflows";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export const MAIL_APP_NAME: Readonly<Record<WorkflowMailApp, string>> = {
  gmail: "Gmail",
  outlook: "Outlook",
};

/** The viewer's own permissions on a workflow, read once and kept as they change them. */
function useWorkflowMailbox(organizationId: string | null, workflowId: string | null) {
  const [access, setAccess] = useState<WorkflowMailboxAccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!organizationId || !workflowId) return;
    let live = true;
    void readWorkflowMailbox(organizationId, workflowId).then((outcome) => {
      if (!live) return;
      if (outcome.ok) setAccess(outcome.value);
      else setError(outcome.message);
    });
    return () => {
      live = false;
    };
  }, [organizationId, workflowId]);
  /** Gives or withdraws one permission; false when it did not take (the error says why). */
  const change = useCallback(
    async (request: WorkflowMailboxAccessRequest): Promise<boolean> => {
      if (!organizationId || !workflowId) return false;
      setBusy(true);
      setError(null);
      const outcome = await setWorkflowMailbox(organizationId, workflowId, request);
      setBusy(false);
      if (outcome.ok) setAccess(outcome.value);
      else setError(outcome.message);
      return outcome.ok;
    },
    [organizationId, workflowId],
  );
  return { access, error, busy, change };
}

/** Whether the viewer has this mailbox connected and ready, as far as the server says. */
const connection = (access: WorkflowMailboxAccessResponse | null, app: WorkflowMailApp) =>
  access?.connected.find((entry) => entry.app === app) ?? null;

/** Beside an email source: whose mailbox it reads, and the viewer's own switch for this workflow. */
export function MailboxPermission({
  organizationId,
  workflowId,
  app,
}: {
  readonly organizationId: string | null;
  readonly workflowId: string | null;
  readonly app: WorkflowMailApp;
}) {
  const { access, error, busy, change } = useWorkflowMailbox(organizationId, workflowId);
  const name = MAIL_APP_NAME[app];
  const connected = connection(access, app);
  const always = access?.always.includes(app) ?? false;
  return (
    <div
      className="space-y-1.5 rounded border border-border/60 p-2"
      data-workflow-mailbox-permission={app}
    >
      <p className="text-[11px] leading-snug text-muted-foreground">
        Each run reads the {name} of the person it runs as, only with their permission, and only
        mail that matches this search. It never sends, changes or deletes anything. Every read is
        logged, and the email text leaves the run after 14 days.
      </p>
      {access === null && !error ? (
        <p className="text-[11px] text-muted-foreground">Checking your {name}…</p>
      ) : connected === null || !connected.ready ? (
        <p className="text-[11px] text-muted-foreground" data-workflow-mailbox-not-connected="">
          Your {name} is not connected. Connect it in Connections to let this workflow read it.
        </p>
      ) : (
        <label className="flex items-center gap-2 text-[12px]">
          <Switch
            checked={always}
            disabled={busy}
            onCheckedChange={(checked) => void change({ app, allow: checked })}
            data-workflow-mailbox-always=""
          />
          {always
            ? `This workflow may read your ${name} in every run`
            : `Ask me in each run before reading my ${name}`}
        </label>
      )}
      {error ? <p className="text-[11px] text-destructive-foreground">{error}</p> : null}
    </div>
  );
}

/**
 * A run waiting at an email Intake step: for the person it runs as, the
 * choice to allow it; for anyone else, who it is waiting for.
 */
export function MailboxWait({
  organizationId,
  run,
  nodeId,
  viewerEmail,
}: {
  readonly organizationId: string;
  readonly run: WorkflowRun;
  readonly nodeId: string;
  readonly viewerEmail: string | null | undefined;
}) {
  const node = run.graph.nodes.find((entry) => entry.id === nodeId);
  const step = run.steps.find((entry) => entry.nodeId === nodeId) ?? null;
  const person = run.startedByEmail?.trim().toLowerCase() ?? null;
  const own = person !== null && viewerEmail?.trim().toLowerCase() === person;
  const { access, error, busy, change } = useWorkflowMailbox(
    own ? organizationId : null,
    own ? run.workflowId : null,
  );
  if (node?.type !== "intake" || step?.status !== "waiting") return null;
  const apps = workflowMailApps(node);
  const names = apps.map((app) => MAIL_APP_NAME[app]).join(" and ");
  if (!own) {
    return (
      <p className="text-[12px] text-muted-foreground" data-workflow-mailbox-wait="">
        Waiting for {person ?? "the person this run acts for"} to let this workflow read their{" "}
        {names}.
      </p>
    );
  }
  const allowAll = async (runId: string | undefined) => {
    // One at a time, stopping at the first refused: its error stays shown, and
    // the run, still missing that mailbox, keeps waiting.
    for (const app of apps) {
      if (!(await change({ app, allow: true, ...(runId ? { runId } : {}) }))) return;
    }
  };
  const missing = apps.filter((app) => !(connection(access, app)?.ready ?? false));
  return (
    <div className="space-y-2" data-workflow-mailbox-wait="">
      <p className="text-[12px] leading-snug">
        This step searches your {names}, and the workflow does not have your permission yet. It only
        searches and reads mail that matches the step's search; it never sends, changes or deletes
        anything.
      </p>
      {missing.length > 0 && access !== null ? (
        <p className="text-[11px] text-muted-foreground">
          Connect your {missing.map((app) => MAIL_APP_NAME[app]).join(" and ")} in Connections
          first, or the step will not be able to read it.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="xs"
          disabled={busy}
          onClick={() => void allowAll(run.id)}
          data-workflow-mailbox-allow-once=""
        >
          <MailIcon />
          Allow for this run
        </Button>
        <Button
          size="xs"
          variant="outline"
          disabled={busy}
          onClick={() => void allowAll(undefined)}
          data-workflow-mailbox-allow-always=""
        >
          Allow for every run
        </Button>
      </div>
      {error ? <p className="text-[11px] text-destructive-foreground">{error}</p> : null}
    </div>
  );
}

/** The mailbox reads of one step of a run, newest last: whose, which search, how many messages. */
export function MailboxReads({
  run,
  nodeId,
}: {
  readonly run: WorkflowRun;
  readonly nodeId: string;
}) {
  const reads = (run.mailboxReads ?? []).filter((read) => read.nodeId === nodeId);
  if (reads.length === 0) return null;
  return (
    <div className="space-y-1" data-workflow-mailbox-reads="">
      <span className="text-[11px] font-medium">Mailbox reads</span>
      <ul className="space-y-1">
        {reads.map((read) => (
          <li
            key={`${read.readAt}:${read.search}`}
            className="text-[11px] leading-snug text-muted-foreground"
          >
            {read.personEmail}'s {MAIL_APP_NAME[read.app]}: {read.messageCount}{" "}
            {read.messageCount === 1 ? "message" : "messages"} for{" "}
            <span className="font-mono">{read.search}</span>, {formatRelativeTimeLabel(read.readAt)}
            .{" "}
            {read.trimmedAt
              ? "The email text has been removed from this run."
              : "The email text leaves this run 14 days after it was read."}
          </li>
        ))}
      </ul>
    </div>
  );
}
